import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { toast } from "react-hot-toast";
import { Permission, type Activity } from "@/types";
import { Card, Badge, brandFillClass } from "@/components/ui";
import { ItemThumbnail } from "@/components/common";
import { MapPin, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { ACTIVITY_TYPE_CONFIG, activityTypeLabel } from "@/constants";
import { resolveCatalogTranslation } from "@/lib/translationResolution";
import { ActivityPartnerShareBadge } from "@/pages/activities/detail/components/ActivityPartnerShareBadge";
import { useAuthStore } from "@/store/authStore";

/** Keep in sync with backend `_INACTIVE_ACTIVITY_ACCESS_PERMISSIONS`. */
const INACTIVE_ACTIVITY_OPEN_PERMISSIONS = [
  Permission.ACTIVITIES_CONFIG_UPDATE,
  Permission.ACTIVITIES_MANAGE_ANY,
  Permission.ACTIVITIES_READ_ANY,
] as const;

interface ActivityCardProps {
  activity: Activity;
  locationLabel?: string;
}

export function ActivityCard({ activity, locationLabel }: ActivityCardProps) {
  const { t, i18n } = useTranslation("activities");
  const { t: tCommon } = useTranslation("common");
  const hasPermission = useAuthStore((state) => state.hasPermission);
  const canOpenInactive = INACTIVE_ACTIVITY_OPEN_PERMISSIONS.some((permission) =>
    hasPermission(permission),
  );
  const canNavigate = activity.is_active || canOpenInactive;
  const config = ACTIVITY_TYPE_CONFIG[activity.category];
  const Icon = config.icon;
  const title = resolveCatalogTranslation(
    activity.name_translations,
    i18n.language,
  );

  const coverImage = activity.media
    ?.filter((item) => item.type === "image" && item.url)
    .sort((a, b) => a.order - b.order)[0]?.url;

  const statusBadge = !activity.is_active ? (
    <Badge variant="default" size="sm" dot>
      {t("card.inactive")}
    </Badge>
  ) : (
    <Badge variant={activity.is_visible ? "success" : "default"} size="sm" dot>
      {activity.is_visible ? t("card.active") : t("card.hidden")}
    </Badge>
  );

  const cardBody = (
    <Card
      className={cn(
        "group flex h-full flex-col overflow-hidden transition-[transform,box-shadow,border-color] duration-200",
        canNavigate
          ? "hover:-translate-y-0.5 hover:border-surface-300 hover:shadow-elevated"
          : "opacity-80",
      )}
    >
      <div className="relative aspect-[2/1] w-full overflow-hidden border-b border-surface-200">
        <ItemThumbnail
          fill
          rounded="none"
          imageUrl={coverImage}
          alt={title}
          fallbackIcon={Icon}
          fallbackIconClassName={cn(
            "h-10 w-10",
            config.color === "accent" && "text-accent-300",
            config.color === "primary" && "text-primary-300",
            config.color === "success" && "text-success-300",
          )}
          className={cn(
            "h-full w-full bg-gradient-to-br",
            config.color === "accent" && "from-accent-50 to-surface-50",
            config.color === "primary" && "from-primary-50 to-surface-50",
            config.color === "success" && "from-success-50 to-surface-50",
          )}
          imgClassName={cn(
            "transition-transform duration-500",
            canNavigate && "group-hover:scale-[1.04]",
          )}
        />

        <div className="absolute left-3 top-3">
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium shadow-sm",
              brandFillClass(config.color),
            )}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden />
            {activityTypeLabel(activity.category)}
          </span>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-3 px-4 py-3.5">
        <div className="min-w-0">
          <h3 className="line-clamp-2 text-[15px] font-semibold tracking-tight text-surface-900">
            {title}
          </h3>
          {locationLabel ? (
            <p className="mt-1 flex items-center gap-1 text-[13px] text-surface-500">
              <MapPin className="h-3.5 w-3.5 shrink-0 text-surface-400" aria-hidden />
              <span className="truncate">{locationLabel}</span>
            </p>
          ) : null}
        </div>

        <div className="mt-auto flex items-center justify-between gap-3 border-t border-surface-200 pt-3">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            {statusBadge}
            <ActivityPartnerShareBadge
              platformCommissionRate={activity.commission_rate}
              labelKey="card.partnerShare"
            />
          </div>

          {canNavigate ? (
            <div className="flex shrink-0 items-center gap-0.5 text-[13px] font-medium text-surface-500 transition-colors group-hover:text-primary-600">
              <span>{tCommon("edit")}</span>
              <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
            </div>
          ) : (
            <span className="shrink-0 text-xs font-medium text-surface-400">
              {t("card.notReadyYet")}
            </span>
          )}
        </div>
      </div>
    </Card>
  );

  if (!canNavigate) {
    return (
      <button
        type="button"
        className="block h-full w-full rounded-xl text-left"
        onClick={() => toast.error(t("card.inactiveBlocked"))}
      >
        {cardBody}
      </button>
    );
  }

  return (
    <Link to={`/activities/${activity.id}`} className="block h-full rounded-xl">
      {cardBody}
    </Link>
  );
}
